import { Ban, CheckCircle2, RotateCcw, Settings, Share2, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ErrorState } from '../../../../components/ErrorState';
import { Menu } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { Skeleton } from '../../../../components/Skeleton';
import { StatusText } from '../../../../components/table/StatusText';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime } from '../../../../lib/formatDate';
import { ChannelModal } from '../components/ChannelModal';
import { pressReleaseChannelsApi } from '../pressReleaseApi';
import { CHANNELS_PATH, PUBLIC_RELATIONS_SETTINGS_PATH, type PressReleaseChannelListItem } from '../pressReleaseTypes';

const BREADCRUMB_BASE: BreadcrumbSegment[] = [
  'Public Relations',
  { label: 'Settings', to: PUBLIC_RELATIONS_SETTINGS_PATH },
  { label: 'Channels', to: CHANNELS_PATH },
];

/**
 * One channel. Thin, and that is the point: what it carries that the form cannot is how many live
 * press releases are ticked as posted on it.
 *
 * **Record-level controls sit beside the status pill**, in the header's cog — Delete (password) and
 * Restore. The banner repeats Restore on a deleted channel; every write confirms.
 */
export function ChannelProfilePage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();

  const [channel, setChannel] = useState<PressReleaseChannelListItem | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [tab, setTab] = useState<'info'>('info');
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState<'delete' | 'restore' | null>(null);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  // A different channel drops what is on screen first.
  const [shownId, setShownId] = useState(id);
  if (shownId !== id) {
    setShownId(id);
    setChannel(null);
    setError(null);
  }

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      pressReleaseChannelsApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;
          setChannel(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this channel.', 0));
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  async function runPending() {
    if (!channel || !pending) return;

    setRunning(true);
    setActionError(null);

    try {
      if (pending === 'delete') await pressReleaseChannelsApi.remove(channel.id);
      else await pressReleaseChannelsApi.restore(channel.id);

      toast.success(`${pending === 'delete' ? 'Deleted' : 'Restored'} ${channel.name}.`);
      setPending(null);
      load();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [...BREADCRUMB_BASE, channel?.name ?? 'Channel'];

  if (error && !channel) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} onRetry={() => load()} />
      </div>
    );
  }

  if (!channel) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-4 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell<'info'>
        id="press-release-channel-profile"
        header={
          <ProfileHeader
            id="press-release-channel-profile-header"
            icon={<Share2 className="h-6 w-6" aria-hidden="true" />}
            title={channel.name}
            meta={[`${channel.postedCount} press ${channel.postedCount === 1 ? 'release' : 'releases'} posted on it`]}
            status={<StatusText tone={channel.isActive ? 'active' : 'inactive'}>{channel.isActive ? 'Active' : 'Deleted'}</StatusText>}
            createdBy={channel.createdByName}
            createdAt={channel.createdAt}
            actions={
              <Menu
                id="press-release-channel-profile-settings"
                items={[
                  channel.isActive
                    ? {
                        key: 'delete',
                        label: 'Delete',
                        icon: <Trash2 className="h-4 w-4" aria-hidden="true" />,
                        permission: 'press-release-channels.delete',
                        destructive: true,
                        onClick: () => setPending('delete'),
                      }
                    : {
                        key: 'restore',
                        label: 'Restore',
                        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
                        permission: 'press-release-channels.delete',
                        onClick: () => setPending('restore'),
                      },
                ]}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    id="press-release-channel-profile-settings-trigger"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Channel settings"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          channel.isActive ? (
            <ProfileBanner
              id="press-release-channel-profile-banner"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This channel is a line on every published press release's checklist."
            />
          ) : (
            <ProfileBanner
              id="press-release-channel-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This channel is deleted. It is off every checklist; press releases already ticked on it keep the tick."
              action={
                <PermissionGate permission="press-release-channels.delete">
                  <Button
                    id="press-release-channel-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => setPending('restore')}
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="press-release-channel-profile-details"
          title="Details"
          onEdit={() => setEditing(true)}
          editPermission="press-release-channels.update"
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Name', value: channel.name },
              { label: 'Description', value: channel.description },
              {
                label: 'Press releases posted on it',
                value: (
                  <span className={`font-mono tabular-nums ${channel.postedCount === 0 ? 'text-slate-300' : ''}`}>
                    {channel.postedCount}
                  </span>
                ),
              },
            ]}
          />
        </ProfileCard>

        <ProfileCard id="press-release-channel-profile-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: channel.createdByName },
              { label: 'Created', value: formatDateTime(channel.createdAt), mono: true },
              { label: 'Updated by', value: channel.updatedByName },
              { label: 'Updated', value: channel.updatedAt != null ? formatDateTime(channel.updatedAt) : null, mono: true },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <ChannelModal
          editing={channel}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      {pending ? (
        <ConfirmDialog
          open
          onClose={() => {
            setPending(null);
            setActionError(null);
          }}
          onConfirm={() => void runPending()}
          title={pending === 'delete' ? 'Delete channel' : 'Restore channel'}
          danger={pending === 'delete'}
          requireConfirm
          requireCredential={pending === 'delete'}
          loading={running}
          id={`press-release-channel-profile-${pending}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {pending === 'delete'
                  ? `"${channel.name}" leaves every press release's checklist. Press releases already ticked on it keep the tick.`
                  : `"${channel.name}" is back on every published press release's checklist.`}
              </p>

              {actionError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}
